import { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import ThemeMaker from "../../components/thememaker";
import { Paginated } from "../../components/Paginated";
import "../../components/table.css"
import { config } from "../../utils/config";
import { tokenDecode } from "../../utils/helper";
import CircularProgress from '@mui/material/CircularProgress';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import brandStyles from "../Brand/Brand.module.css";
import styles from "./Rider.module.css";
import "../../components/mcrbTableTheme.css";
import "../../index.css"

const { baseNodeapiUrl } = config;

const toastOpts: any = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
};

const RiderList = () => {
    const history = useHistory();
    const decode = tokenDecode();
    const { data } = decode;
    const riderPermission = data?.permission?.rider;
    const [riderList, setRiderList] = useState<any[]>([]);
    const [orderCounts, setOrderCounts] = useState<any>({});
    const [loading, setLoading] = useState(true);

    const fetchRiderList = async () => {
        try {
            const { data } = await axios({
                url: `${baseNodeapiUrl}/invlrider/getrider`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`
                }
            });
            if (data.success) {
                setRiderList(data.data || []);
            } else {
                toast.error(data.message || "Failed to fetch riders !", toastOpts);
            }
        } catch (error) {
            toast.error("Something went wrong while fetching riders !", toastOpts);
        }
    }

    const fetchOrderCounts = async () => {
        try {
            const { data } = await axios({
                url: `${baseNodeapiUrl}/invlrider/ordersrider`,
                method: "POST",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`
                },
                data: {}
            });
            if (data.success) {
                const counts: any = {};
                (data.data || []).forEach((item: any) => {
                    const riderId = item.rider_id;
                    if (riderId === undefined || riderId === null) return;
                    counts[riderId] = item;
                });
                setOrderCounts(counts);
            }
        } catch (error) {
        }
    }

    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([fetchRiderList(), fetchOrderCounts()]);
            setLoading(false);
        })();
    }, []);

    const COLUMNS = [
        {
            Header: "Sn.",
            Footer: "Sn.",
            accessor: (row: any, count: any) => {
                return <>{count + 1}</>;
            },
            disableFilters: true,
            sticky: "left",
        },
        {
            Header: "Name",
            Footer: "Name",
            accessor: "name",
            sticky: "left",
        },
        {
            Header: "WhatsApp Number",
            Footer: "WhatsApp Number",
            accessor: "whatsapp_number",
            sticky: "left",
        },
        {
            Header: "Email",
            Footer: "Email",
            accessor: "email",
            sticky: "left",
        },
        {
            Header: "Address",
            Footer: "Address",
            accessor: "address",
            sticky: "left",
        },
        {
            Header: "Status",
            Footer: "Status",
            accessor: (row: any) => (
                <span className={row.status ? brandStyles.statusBadgeActive : brandStyles.statusBadgeInactive}>
                    {row.status ? "Active" : "Inactive"}
                </span>
            ),
            sticky: "left",
        },
        {
            Header: "Orders",
            Footer: "Orders",
            accessor: (row: any) => (
                <span
                    className={styles.orderCountLink}
                    title={`Delivered: ${orderCounts[row.id]?.delivered_orders ?? 0} · Active: ${orderCounts[row.id]?.active_orders ?? 0}`}
                    onClick={() => history.push(`/riderorders/${row.id}`)}
                >
                    {orderCounts[row.id]?.total_orders ?? 0}
                </span>
            ),
            sticky: "left",
        },
        {
            Header: "Action",
            Footer: "Action",
            accessor: (row: any) => (
                <div className="d-flex" style={{ gap: "10px" }}>
                    {riderPermission?.update ? (
                        <span className={brandStyles.iconBtn} onClick={() => history.push(`/updaterider/${row.id}`)}>
                            <EditNoteRoundedIcon style={{ color: "#4c2fb3" }} titleAccess="Update" />
                        </span>
                    ) : null}
                </div>
            ),
            sticky: "left",
        },
    ];

    return (
        <ThemeMaker>
            <div className={`${styles.pageWrap} mcrb-page`}>
                <div className={`${brandStyles.cardOuter} mcrb-card`}>
                    <div className={brandStyles.headerBar}>
                        <span className={brandStyles.title}>Rider List</span>
                        {riderPermission?.write ? (
                            <Link to="/addrider" className={brandStyles.primaryBtn}>
                                Add Rider
                            </Link>
                        ) : null}
                    </div>
                    <div className={`${brandStyles.bodyArea} ${brandStyles.tableWrap}`} style={{ minHeight: "60vh" }}>
                        {
                            loading ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
                                    <CircularProgress />
                                </div>
                            ) : (
                                <>
                                    <div className={styles.desktopTable}>
                                        <Paginated data={riderList} columns={COLUMNS} />
                                    </div>

                                    <div className={styles.mobileList}>
                                        {
                                            riderList.map((item: any) => (
                                                <div className={styles.mobileCard} key={item.id}>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Name</span>
                                                        <span className={styles.mobileValue}>{item?.name}</span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>WhatsApp</span>
                                                        <span className={styles.mobileValue}>{item?.whatsapp_number}</span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Address</span>
                                                        <span className={styles.mobileValue}>{item?.address}</span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Status</span>
                                                        <span className={item?.status ? brandStyles.statusBadgeActive : brandStyles.statusBadgeInactive}>
                                                            {item?.status ? "Active" : "Inactive"}
                                                        </span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Orders</span>
                                                        <span className={styles.orderCountLink} onClick={() => history.push(`/riderorders/${item.id}`)}>
                                                            {orderCounts[item.id]?.total_orders ?? 0}
                                                        </span>
                                                    </div>
                                                    <div className={styles.mobileActionRow}>
                                                        {riderPermission?.update ? (
                                                            <span className={brandStyles.iconBtn} onClick={() => history.push(`/updaterider/${item.id}`)}>
                                                                <EditNoteRoundedIcon style={{ color: "#4c2fb3" }} titleAccess="Update" />
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>

                                    {riderList.length === 0 ? <p className={styles.emptyState}>No riders found !</p> : null}
                                </>
                            )
                        }
                    </div>
                </div>
            </div>
        </ThemeMaker>
    )
}

export default RiderList;
